/**
 * Viewer URL state — `?mode=browse|detail|theater&car=<device_id>&stage=<i>`
 * (handover §3: one layout, three modes, URL-addressable).
 *
 * Mode transitions push history entries so the browser back button
 * walks browse → detail → theater naturally. The `car` param survives
 * a return to browse so the Driver tab keeps its last car. `stage`
 * scopes the viewer to one stage's footage (StageStrip); absent = All.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type ViewerMode = 'browse' | 'detail' | 'theater';

const MODES: ReadonlySet<string> = new Set(['browse', 'detail', 'theater']);

export interface ViewerUrlState {
  mode: ViewerMode;
  /** Selected device_id from `?car=` — null when absent. */
  carId: string | null;
  /** Selected stage index from `?stage=` — null when absent/invalid. */
  stageIndex: number | null;
  /** Set mode (keeps current car). */
  setMode: (mode: ViewerMode) => void;
  /** Select a car; default jumps to detail mode (pin/row click). */
  selectCar: (deviceId: string, mode?: ViewerMode) => void;
  /** Scope to a stage (null = All). Replaces history — chip taps
   *  shouldn't stack back-button entries. */
  setStageIndex: (index: number | null) => void;
}

export function useViewerUrlState(): ViewerUrlState {
  const [params, setParams] = useSearchParams();

  const rawMode = params.get('mode') ?? 'browse';
  const mode: ViewerMode = MODES.has(rawMode) ? (rawMode as ViewerMode) : 'browse';
  const carId = params.get('car');
  const rawStage = params.get('stage');
  const parsedStage = rawStage == null ? NaN : Number(rawStage);
  // Same bounds as the backend stage_index field (ge=0, le=63).
  const stageIndex =
    Number.isInteger(parsedStage) && parsedStage >= 0 && parsedStage <= 63
      ? parsedStage
      : null;

  const setMode = useCallback(
    (next: ViewerMode) => {
      setParams((prev) => {
        const out = new URLSearchParams(prev);
        if (next === 'browse') out.delete('mode');
        else out.set('mode', next);
        return out;
      });
    },
    [setParams],
  );

  const selectCar = useCallback(
    (deviceId: string, nextMode: ViewerMode = 'detail') => {
      setParams((prev) => {
        const out = new URLSearchParams(prev);
        out.set('car', deviceId);
        if (nextMode === 'browse') out.delete('mode');
        else out.set('mode', nextMode);
        return out;
      });
    },
    [setParams],
  );

  const setStageIndex = useCallback(
    (index: number | null) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (index == null) out.delete('stage');
          else out.set('stage', String(index));
          return out;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return useMemo(
    () => ({ mode, carId, stageIndex, setMode, selectCar, setStageIndex }),
    [mode, carId, stageIndex, setMode, selectCar, setStageIndex],
  );
}
